import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import { EmptyState, FormSection, SaveStatus, type SaveStatusKind } from '@redhand/ui';
import { DomainApiError } from '../lib/api-error.js';
import { humanApiErrorText } from '../lib/form-errors.js';
import { CLIENT_MAIL_COPY_HINT_KEY, clientMailReasonKey } from '../lib/client-mail-message.js';
import {
  compareInvitationWithLv,
  confirmClientReceipt,
  createClientInvitation,
  processInvitationDocuments,
  getClientInvitation,
  listClientInvitations,
  remindClientInvitation,
  revokeClientInvitation,
  type ClientChannel,
  type ClientIntakeDocumentDto,
  type ClientIntakePropertyDto,
  type ClientInvitationDetailDto,
  type ClientInvitationDto,
  type ClientPartyKey,
  type ClientRole,
  type ConfirmReceiptResultDto,
  type CreateClientInvitationBody,
  type IntakeDocumentKind,
  type IntakeDocumentStatus,
  type LvFindingDto,
  type MissingDocumentDto,
  type PropertyFindingCode,
  type PropertyFindingDto,
} from '../lib/client-intake-api.js';
import { DocumentCheckCard } from './DocumentCheckCard.js';
import type { PartyInvitePrefill } from '../lib/party-invites.js';
import { getContract, type ContractDetailDto } from '../lib/contracts-api.js';

/**
 * Zalozka "Klient (magic link)" (krok KP1, vzor obrazovek R.B. 27. 9. 22:00).
 *
 * Makler tu vidi: komu se odkaz posila, co ma klient udelat, kanal, platnost
 * a casovou osu odkazu; po odeslani klientem shrnuti udaju (rodne cislo
 * a cislo dokladu VZDY maskovane - API jine nevraci), nalezy porovnani s LV
 * a tlacitko "Potvrdit prevzeti", ktere udaje propise do smlouvy.
 *
 * ODKAZ SE ZOBRAZI JEDINKRAT: v DB je jen hash tokenu, takze po zavreni
 * panelu ho uz nikdo neprecte - makler ho musi zkopirovat nebo poslat kanalem.
 */

const LOCALE = 'cs' as const;

/*
 * Krok SML5 bod 3: u ZNAMEHO duvodu (`type` v problem+json) ma prednost veta,
 * ktera rika CO udelat — server u 4xx posila obecny titulek „Validace vstupu
 * selhala". Mapa duvodu je jedna pro vsechny formulare (`lib/form-errors.ts`).
 */
function errorMessage(error: unknown): string | undefined {
  const lidsky = humanApiErrorText(error);
  if (lidsky !== undefined) return lidsky;
  return error instanceof DomainApiError ? error.message : undefined;
}

const ROLE_KEY: Readonly<Record<ClientRole, MessageKey>> = {
  prodavajici: 'webAdmin.clientIntake.role.prodavajici',
  pronajimatel: 'webAdmin.clientIntake.role.pronajimatel',
  spoluvlastnik: 'webAdmin.clientIntake.role.spoluvlastnik',
  'zajemce-kupujici': 'webAdmin.clientIntake.role.zajemce-kupujici',
  najemce: 'webAdmin.clientIntake.role.najemce',
  'zastupce-firmy': 'webAdmin.clientIntake.role.zastupce-firmy',
};

const STATUS_KEY: Readonly<Record<ClientInvitationDto['status'], MessageKey>> = {
  odeslano: 'webAdmin.clientIntake.status.odeslano',
  otevreno: 'webAdmin.clientIntake.status.otevreno',
  rozpracovano: 'webAdmin.clientIntake.status.rozpracovano',
  'odeslano-klientem': 'webAdmin.clientIntake.status.odeslano-klientem',
  'zkontrolovano-maklerem': 'webAdmin.clientIntake.status.zkontrolovano-maklerem',
  zneplatneno: 'webAdmin.clientIntake.status.zneplatneno',
};

const CHANNEL_KEY: Readonly<Record<ClientChannel, MessageKey>> = {
  email: 'webAdmin.clientIntake.channel.email',
  sms: 'webAdmin.clientIntake.channel.sms',
  odkaz: 'webAdmin.clientIntake.channel.odkaz',
};

const PARTY_KEY_LABEL: Readonly<Record<ClientPartyKey, MessageKey>> = {
  prodavajici: 'webAdmin.clientIntake.partyKey.prodavajici',
  kupujici: 'webAdmin.clientIntake.partyKey.kupujici',
  pronajimatel: 'webAdmin.clientIntake.partyKey.pronajimatel',
  najemce: 'webAdmin.clientIntake.partyKey.najemce',
};

const LV_FINDING_KEY: Readonly<Record<LvFindingDto['code'], MessageKey>> = {
  'jmeno-neni-na-lv': 'webAdmin.clientIntake.lv.jmeno-neni-na-lv',
  'podil-neodpovida-lv': 'webAdmin.clientIntake.lv.podil-neodpovida-lv',
  'podil-neuveden': 'webAdmin.clientIntake.lv.podil-neuveden',
  'lv-bez-vlastniku': 'webAdmin.clientIntake.lv.lv-bez-vlastniku',
};

/** Popisky udalosti casove osy odkazu (vzor obrazovek: "Casova osa odkazu"). */
const TIMELINE_KEY: Readonly<Record<string, MessageKey>> = {
  vytvoreno: 'webAdmin.clientIntake.timeline.vytvoreno',
  odeslano: 'webAdmin.clientIntake.timeline.odeslano',
  pripominka: 'webAdmin.clientIntake.timeline.pripominka',
  otevreno: 'webAdmin.clientIntake.timeline.otevreno',
  'sms-overeno': 'webAdmin.clientIntake.timeline.sms-overeno',
  'sms-selhalo': 'webAdmin.clientIntake.timeline.sms-selhalo',
  'doklad-vytezen': 'webAdmin.clientIntake.timeline.doklad-vytezen',
  'doklad-nevytezen': 'webAdmin.clientIntake.timeline.doklad-nevytezen',
  ulozeno: 'webAdmin.clientIntake.timeline.ulozeno',
  'odeslano-klientem': 'webAdmin.clientIntake.timeline.odeslano-klientem',
  zkontrolovano: 'webAdmin.clientIntake.timeline.zkontrolovano',
  zneplatneno: 'webAdmin.clientIntake.timeline.zneplatneno',
  'pozvanka-manzela': 'webAdmin.clientIntake.timeline.pozvanka-manzela',
};

// ------------------------------------------------------------------ krok KP2

const DOCUMENT_KIND_KEY: Readonly<Record<IntakeDocumentKind, MessageKey>> = {
  'nabyvaci-titul': 'webAdmin.clientIntake.documentKind.nabyvaci-titul',
  penb: 'webAdmin.clientIntake.documentKind.penb',
  kolaudace: 'webAdmin.clientIntake.documentKind.kolaudace',
  'stavebni-povoleni': 'webAdmin.clientIntake.documentKind.stavebni-povoleni',
  pudorys: 'webAdmin.clientIntake.documentKind.pudorys',
  'revize-plyn': 'webAdmin.clientIntake.documentKind.revize-plyn',
  'revize-elektro': 'webAdmin.clientIntake.documentKind.revize-elektro',
  'revize-komin': 'webAdmin.clientIntake.documentKind.revize-komin',
  'prohlaseni-vlastnika': 'webAdmin.clientIntake.documentKind.prohlaseni-vlastnika',
  'najemni-smlouva': 'webAdmin.clientIntake.documentKind.najemni-smlouva',
  fotka: 'webAdmin.clientIntake.documentKind.fotka',
  jine: 'webAdmin.clientIntake.documentKind.jine',
};

const DOCUMENT_STATUS_KEY: Readonly<Record<IntakeDocumentStatus, MessageKey>> = {
  'ceka-na-nahrani': 'webAdmin.clientIntake.documentStatus.ceka-na-nahrani',
  'nahrava-se': 'webAdmin.clientIntake.documentStatus.nahrava-se',
  'kontrola-viru': 'webAdmin.clientIntake.documentStatus.kontrola-viru',
  ulozeno: 'webAdmin.clientIntake.documentStatus.ulozeno',
  zamitnuto: 'webAdmin.clientIntake.documentStatus.zamitnuto',
};

const FINDING_KEY: Readonly<Record<PropertyFindingCode, MessageKey>> = {
  'parcela-navic-proti-lv': 'webAdmin.clientIntake.finding.parcela-navic-proti-lv',
  'parcela-chybi-proti-lv': 'webAdmin.clientIntake.finding.parcela-chybi-proti-lv',
  'jednotka-neodpovida-lv': 'webAdmin.clientIntake.finding.jednotka-neodpovida-lv',
  'podil-jednotky-neodpovida-lv': 'webAdmin.clientIntake.finding.podil-jednotky-neodpovida-lv',
  'spoluvlastnik-neni-na-lv': 'webAdmin.clientIntake.finding.spoluvlastnik-neni-na-lv',
  'lv-nepotvrzeno-klientem': 'webAdmin.clientIntake.finding.lv-nepotvrzeno-klientem',
  'hypoteka-bez-zastavy-na-lv': 'webAdmin.clientIntake.finding.hypoteka-bez-zastavy-na-lv',
  'zastava-na-lv-bez-hypoteky': 'webAdmin.clientIntake.finding.zastava-na-lv-bez-hypoteky',
  'svj-uver-neuveden': 'webAdmin.clientIntake.finding.svj-uver-neuveden',
  'svj-fond-oprav-neodpovida-zaverce':
    'webAdmin.clientIntake.finding.svj-fond-oprav-neodpovida-zaverce',
  'svj-zaverka-zastarala': 'webAdmin.clientIntake.finding.svj-zaverka-zastarala',
  'svj-bez-zaverky': 'webAdmin.clientIntake.finding.svj-bez-zaverky',
  'plocha-neodpovida-zakazce': 'webAdmin.clientIntake.finding.plocha-neodpovida-zakazce',
  'dispozice-neodpovida-zakazce': 'webAdmin.clientIntake.finding.dispozice-neodpovida-zakazce',
  'najemne-neodpovida-zakazce': 'webAdmin.clientIntake.finding.najemne-neodpovida-zakazce',
  'cena-neodpovida-zakazce': 'webAdmin.clientIntake.finding.cena-neodpovida-zakazce',
};

const SEVERITY_KEY: Readonly<Record<PropertyFindingDto['severity'], MessageKey>> = {
  rozpor: 'webAdmin.clientIntake.severity.rozpor',
  upozorneni: 'webAdmin.clientIntake.severity.upozorneni',
};

/** ANO / NE / nevyplneno. "Nevyplneno" NENI "ne" - viz `property.ts`. */
function yesNoLabel(value: boolean | null): string {
  if (value === null) return '—';
  return value ? t('common.yes', { locale: LOCALE }) : t('common.no', { locale: LOCALE });
}

function numberLabel(value: number | null): string {
  return value === null ? '—' : String(value);
}

function textLabel(value: string | null): string {
  return value === null || value === '' ? '—' : value;
}

/**
 * Shrnuti dotazniku k nemovitosti (krok KP2). Makler tu vidi JEN to, co potom
 * pujde do smlouvy nebo na co se musi zeptat - uplny tvar odpovedi je v poli
 * smlouvy po prevzeti, ne tady.
 */
function PropertySummary({
  property,
  sections,
}: {
  readonly property: ClientIntakePropertyDto;
  readonly sections: readonly string[];
}): React.JSX.Element | null {
  if (sections.length === 0) return null;
  const facts = property.facts;
  return (
    <div data-testid="client-intake-property">
      <h4 className="font-medium mt-3">
        {t('webAdmin.clientIntake.propertyHeading', { locale: LOCALE })}
      </h4>
      <dl className="grid grid-cols-2 gap-1">
        {sections.includes('nemovitost') && (
          <>
            <dt>{t('webAdmin.clientIntake.property.lvConfirmed', { locale: LOCALE })}</dt>
            <dd>{yesNoLabel(facts.lvConfirmed)}</dd>
            <dt>{t('webAdmin.clientIntake.property.layout', { locale: LOCALE })}</dt>
            <dd>{textLabel(facts.layout)}</dd>
            <dt>{t('webAdmin.clientIntake.property.floorArea', { locale: LOCALE })}</dt>
            <dd>{numberLabel(facts.floorAreaM2)}</dd>
            <dt>{t('webAdmin.clientIntake.property.floor', { locale: LOCALE })}</dt>
            <dd>{numberLabel(facts.floor)}</dd>
            <dt>{t('webAdmin.clientIntake.property.coOwners', { locale: LOCALE })}</dt>
            <dd>
              {facts.coOwners.length === 0
                ? '—'
                : facts.coOwners
                    .map((owner) =>
                      owner.share === null ? owner.fullName : `${owner.fullName} (${owner.share})`,
                    )
                    .join(', ')}
            </dd>
          </>
        )}
        {sections.includes('zavazky') && (
          <>
            <dt>{t('webAdmin.clientIntake.property.mortgage', { locale: LOCALE })}</dt>
            <dd>
              {yesNoLabel(property.obligations.mortgage.present)}
              {property.obligations.mortgage.bank !== null
                ? ` · ${property.obligations.mortgage.bank}`
                : ''}
            </dd>
            <dt>{t('webAdmin.clientIntake.property.execution', { locale: LOCALE })}</dt>
            <dd>{yesNoLabel(property.obligations.executionPresent)}</dd>
            <dt>{t('webAdmin.clientIntake.property.tenants', { locale: LOCALE })}</dt>
            <dd>{yesNoLabel(property.obligations.tenantsPresent)}</dd>
            <dt>{t('webAdmin.clientIntake.property.disputes', { locale: LOCALE })}</dt>
            <dd>{yesNoLabel(property.obligations.disputesPresent)}</dd>
          </>
        )}
        {sections.includes('svj') && (
          <>
            <dt>{t('webAdmin.clientIntake.property.hoa', { locale: LOCALE })}</dt>
            <dd>{textLabel(property.hoa.kind)}</dd>
            <dt>{t('webAdmin.clientIntake.property.hoaContact', { locale: LOCALE })}</dt>
            <dd>{textLabel(property.hoa.managerContact)}</dd>
            <dt>{t('webAdmin.clientIntake.property.hoaAdvance', { locale: LOCALE })}</dt>
            <dd>{numberLabel(property.hoa.monthlyAdvanceCzk)}</dd>
            <dt>{t('webAdmin.clientIntake.property.hoaDebt', { locale: LOCALE })}</dt>
            <dd>{numberLabel(property.hoa.debtToHoaCzk)}</dd>
          </>
        )}
        {sections.includes('pronajem') && (
          <>
            <dt>{t('webAdmin.clientIntake.property.rent', { locale: LOCALE })}</dt>
            <dd>{numberLabel(property.rental.monthlyRentCzk)}</dd>
            <dt>{t('webAdmin.clientIntake.property.deposit', { locale: LOCALE })}</dt>
            <dd>{numberLabel(property.rental.depositCzk)}</dd>
            <dt>{t('webAdmin.clientIntake.property.availableFrom', { locale: LOCALE })}</dt>
            <dd>{textLabel(property.rental.availableFrom)}</dd>
          </>
        )}
        {sections.includes('financovani') && (
          <>
            <dt>{t('webAdmin.clientIntake.property.financing', { locale: LOCALE })}</dt>
            <dd>
              {textLabel(property.buyer.financing)}
              {property.buyer.bank !== null ? ` · ${property.buyer.bank}` : ''}
            </dd>
          </>
        )}
        {sections.includes('najemce') && (
          <>
            <dt>{t('webAdmin.clientIntake.property.occupants', { locale: LOCALE })}</dt>
            <dd>{numberLabel(property.tenant.occupants)}</dd>
            <dt>{t('webAdmin.clientIntake.property.moveInFrom', { locale: LOCALE })}</dt>
            <dd>{textLabel(property.tenant.moveInFrom)}</dd>
          </>
        )}
      </dl>

      {sections.includes('nemovitost') && (
        <div data-testid="client-intake-defects">
          <h5 className="font-medium mt-2">
            {t('webAdmin.clientIntake.property.defects', { locale: LOCALE })}
          </h5>
          {facts.noKnownDefects === true ? (
            <p>{t('webAdmin.clientIntake.property.noDefects', { locale: LOCALE })}</p>
          ) : facts.defects.length === 0 ? (
            <p>—</p>
          ) : (
            <ul>
              {facts.defects.map((defect, index) => (
                <li key={`${defect.description}-${index}`}>
                  {defect.description}
                  {defect.sinceYear !== null ? ` (${String(defect.sinceYear)})` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** Rozpory s LV / SVJ / zakazkou. `rozpor` blokuje, `upozorneni` jen rika. */
function PropertyFindings({
  findings,
}: {
  readonly findings: readonly PropertyFindingDto[];
}): React.JSX.Element | null {
  if (findings.length === 0) return null;
  return (
    <div data-testid="client-intake-property-findings">
      <h4 className="font-medium mt-3">
        {t('webAdmin.clientIntake.findingsHeading', { locale: LOCALE })}
      </h4>
      <ul>
        {findings.map((finding, index) => (
          <li
            key={`${finding.code}-${index}`}
            className={finding.severity === 'rozpor' ? 'rh-text-danger' : 'rh-text-warning'}
          >
            {t(SEVERITY_KEY[finding.severity], { locale: LOCALE })}
            {': '}
            {t(FINDING_KEY[finding.code], { locale: LOCALE })}
            {finding.detail !== null ? ` (${finding.detail})` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Nahrane dokumenty a soupis chybejicich (= ukoly pro maklere, bod 4). */
function IntakeDocuments({
  documents,
  missing,
  onExtract,
  extractNote,
}: {
  readonly documents: readonly ClientIntakeDocumentDto[];
  readonly missing: readonly MissingDocumentDto[];
  readonly onExtract: () => void;
  readonly extractNote: string | null;
}): React.JSX.Element {
  return (
    <div data-testid="client-intake-documents">
      <h4 className="font-medium mt-3">
        {t('webAdmin.clientIntake.documentsHeading', { locale: LOCALE })}
      </h4>
      {documents.length === 0 ? (
        <p>{t('webAdmin.clientIntake.documentsEmpty', { locale: LOCALE })}</p>
      ) : (
        <ul>
          {documents.map((doc) => (
            <li key={doc.id} data-testid={`client-intake-document-${doc.kind}`}>
              {t(DOCUMENT_KIND_KEY[doc.kind], { locale: LOCALE })} —{' '}
              {t(DOCUMENT_STATUS_KEY[doc.status], { locale: LOCALE })}
              {Object.keys(doc.extracted).length > 0
                ? ` · ${Object.entries(doc.extracted)
                    .map(([key, value]) => `${key}: ${String(value)}`)
                    .join(', ')}`
                : ''}
            </li>
          ))}
        </ul>
      )}

      {missing.length > 0 && (
        <div data-testid="client-intake-documents-missing">
          <h5 className="font-medium mt-2">
            {t('webAdmin.clientIntake.documentsMissing', { locale: LOCALE })}
          </h5>
          <ul>
            {missing.map((item) => (
              <li key={item.kind} className={item.required ? 'rh-text-warning' : undefined}>
                {t(DOCUMENT_KIND_KEY[item.kind as IntakeDocumentKind], { locale: LOCALE })}
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        className="rh-btn rh-btn--ghost mt-2"
        onClick={onExtract}
        data-testid="client-intake-extract"
      >
        {t('webAdmin.clientIntake.extractButton', { locale: LOCALE })}
      </button>
      {extractNote !== null && <p data-testid="client-intake-extract-note">{extractNote}</p>}
    </div>
  );
}

function formatDate(iso: string | null): string {
  if (iso === null || iso === '') return '—';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'medium', timeStyle: 'short' }).format(parsed);
}

const ROLES: readonly ClientRole[] = [
  'prodavajici',
  'pronajimatel',
  'spoluvlastnik',
  'zajemce-kupujici',
  'najemce',
  'zastupce-firmy',
];

const PARTY_KEYS: readonly ClientPartyKey[] = [
  'prodavajici',
  'kupujici',
  'pronajimatel',
  'najemce',
];

export interface ClientIntakeScreenProps {
  readonly contractId?: string | undefined;
  readonly caseId?: string | undefined;
  /**
   * Krok UXP3 (P1-6): udaje strany smlouvy, ktera jeste pozvanku nema.
   * Zalozka se do UXP3 ptala na jmeno a e-mail znovu, prestoze strana je uz
   * mela vyplnene.
   */
  readonly prefill?: PartyInvitePrefill | null | undefined;
}

export function ClientIntakeScreen({
  contractId,
  caseId,
  prefill,
}: ClientIntakeScreenProps): React.JSX.Element {
  const [rows, setRows] = React.useState<ClientInvitationDto[] | null>(null);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [openId, setOpenId] = React.useState<string | null>(null);
  /** Odkaz vraceny prave zalozenou/preposlanou pozvankou - jen v pameti. */
  const [issuedLink, setIssuedLink] = React.useState<{
    id: string;
    link: string;
    /** Krok MAIL1 bod 3: `null` = odkaz odešel e-mailem. */
    notDeliveredReason: string | null;
  } | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setRows(null);
    listClientInvitations({
      ...(contractId !== undefined ? { contractId } : {}),
      ...(caseId !== undefined ? { caseId } : {}),
    })
      .then((data) => {
        if (!cancelled) setRows(data);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(errorMessage(err) ?? t('webAdmin.clientIntake.loadError', { locale: LOCALE }));
      });
    return () => {
      cancelled = true;
    };
  }, [contractId, caseId, refreshKey]);

  return (
    <div data-testid="client-intake-screen">
      <FormSection
        title={t('webAdmin.clientIntake.heading', { locale: LOCALE })}
        description={t('webAdmin.clientIntake.perex', { locale: LOCALE })}
      >
        <NewInvitationForm
          // Predvyplnene udaje chodi az po nacteni smlouvy; `key` je proto
          // soucasti identity formulare (stav z props v efektu je zavod —
          // nalez UXS2/kokpit, viz docs/platform/TESTOVANI.md).
          key={prefill === null || prefill === undefined ? 'bez-strany' : `${prefill.partyKey}-${String(prefill.partyIndex)}`}
          contractId={contractId}
          caseId={caseId}
          prefill={prefill ?? null}
          onIssued={(id, link, notDeliveredReason) => {
            setIssuedLink({ id, link, notDeliveredReason: notDeliveredReason ?? null });
            setRefreshKey((key) => key + 1);
          }}
        />
      </FormSection>

      {issuedLink !== null && (
        <IssuedLinkBox
          link={issuedLink.link}
          notDeliveredReason={issuedLink.notDeliveredReason ?? null}
          onDismiss={() => setIssuedLink(null)}
        />
      )}

      {error !== undefined && (
        <p className="rh-text-danger" role="alert">
          {error}
        </p>
      )}

      {rows !== null && rows.length === 0 && (
        <EmptyState title={t('webAdmin.clientIntake.empty', { locale: LOCALE })} />
      )}

      {rows !== null && rows.length > 0 && (
        <table className="rh-table w-full" data-testid="client-intake-table">
          <thead>
            <tr>
              <th>{t('webAdmin.clientIntake.table.recipient', { locale: LOCALE })}</th>
              <th>{t('webAdmin.clientIntake.table.role', { locale: LOCALE })}</th>
              <th>{t('webAdmin.clientIntake.table.status', { locale: LOCALE })}</th>
              <th>{t('webAdmin.clientIntake.table.expires', { locale: LOCALE })}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} data-testid={`client-intake-row-${row.id}`}>
                <td>
                  {row.recipientName}
                  {row.spouseOfInvitationId !== null && (
                    <span className="rh-badge ml-2">
                      {t('webAdmin.clientIntake.spouseBadge', { locale: LOCALE })}
                    </span>
                  )}
                </td>
                <td>{t(ROLE_KEY[row.role], { locale: LOCALE })}</td>
                <td>{t(STATUS_KEY[row.status], { locale: LOCALE })}</td>
                <td>{formatDate(row.expiresAt)}</td>
                <td>
                  <button
                    type="button"
                    className="rh-btn rh-btn--ghost"
                    onClick={() => setOpenId(openId === row.id ? null : row.id)}
                  >
                    {t('webAdmin.clientIntake.detailButton', { locale: LOCALE })}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {openId !== null && (
        <InvitationDetail
          invitationId={openId}
          contractId={contractId}
          onChanged={() => setRefreshKey((key) => key + 1)}
          onIssued={(id, link, notDeliveredReason) =>
            setIssuedLink({ id, link, notDeliveredReason: notDeliveredReason ?? null })
          }
        />
      )}
    </div>
  );
}

/**
 * Odkaz pro klienta. Zobrazuje se JEDINKRAT (v DB je jen hash) - proto je
 * v panelu vyrazne a s tlacitkem na zkopirovani.
 */
function IssuedLinkBox({
  link,
  notDeliveredReason,
  onDismiss,
}: {
  readonly link: string;
  /** Krok MAIL1 bod 3: `null` = odesláno (nebo kanál „jen zkopírovat"). */
  readonly notDeliveredReason: string | null;
  readonly onDismiss: () => void;
}): React.JSX.Element {
  const [copied, setCopied] = React.useState(false);
  return (
    <div className="rh-callout" data-testid="client-intake-link">
      {/* MAIL1 bod 3: nejdřív PRAVDA o odeslání, pak odkaz. Tiché „odesláno"
          by makléře nechalo čekat na klienta, který nic nedostal. */}
      {notDeliveredReason !== null && (
        <p className="rh-text-danger" role="alert" data-testid="client-intake-not-sent">
          {`${t(clientMailReasonKey(notDeliveredReason), { locale: LOCALE })} ${t(CLIENT_MAIL_COPY_HINT_KEY, { locale: LOCALE })}`}
        </p>
      )}
      <p className="font-medium">{t('webAdmin.clientIntake.linkHeading', { locale: LOCALE })}</p>
      <p className="rh-text-muted text-sm">
        {t('webAdmin.clientIntake.linkOnce', { locale: LOCALE })}
      </p>
      <code className="block break-all my-2">{link}</code>
      <div className="flex gap-2">
        <button
          type="button"
          className="rh-btn rh-btn--secondary"
          onClick={() => {
            void navigator.clipboard?.writeText(link).then(
              () => setCopied(true),
              () => setCopied(false),
            );
          }}
        >
          {t('webAdmin.clientIntake.copyButton', { locale: LOCALE })}
        </button>
        <button type="button" className="rh-btn rh-btn--ghost" onClick={onDismiss}>
          {t('webAdmin.clientIntake.dismissButton', { locale: LOCALE })}
        </button>
        {copied && (
          <span className="rh-text-muted text-sm">
            {t('webAdmin.clientIntake.copied', { locale: LOCALE })}
          </span>
        )}
      </div>
    </div>
  );
}

function NewInvitationForm({
  contractId,
  caseId,
  prefill,
  onIssued,
}: {
  readonly contractId: string | undefined;
  readonly caseId: string | undefined;
  readonly prefill: PartyInvitePrefill | null;
  readonly onIssued: (id: string, link: string, notDeliveredReason?: string | null) => void;
}): React.JSX.Element {
  const [role, setRole] = React.useState<ClientRole>(prefill?.role ?? 'prodavajici');
  const [partyKey, setPartyKey] = React.useState<ClientPartyKey | ''>(prefill?.partyKey ?? '');
  const [partyIndex, setPartyIndex] = React.useState(prefill?.partyIndex ?? 0);
  const [recipientName, setRecipientName] = React.useState(prefill?.recipientName ?? '');
  const [email, setEmail] = React.useState(prefill?.email ?? '');
  const [phone, setPhone] = React.useState(prefill?.phone ?? '');
  const [ttlDays, setTtlDays] = React.useState(7);
  const [requireSms, setRequireSms] = React.useState(false);
  const [status, setStatus] = React.useState<SaveStatusKind>('idle');
  const [error, setError] = React.useState<string | undefined>(undefined);

  async function submit(channel: ClientChannel): Promise<void> {
    setStatus('saving');
    setError(undefined);
    const body: CreateClientInvitationBody = {
      role,
      recipientName,
      channel,
      ttlDays,
      requireSms,
      ...(contractId !== undefined ? { contractId } : {}),
      ...(caseId !== undefined ? { caseId } : {}),
      ...(partyKey !== '' ? { partyKey } : {}),
      ...(partyIndex > 0 ? { partyIndex } : {}),
      ...(email !== '' ? { recipientEmail: email } : {}),
      ...(phone !== '' ? { recipientPhone: phone } : {}),
    };
    try {
      const issued = await createClientInvitation(body);
      setStatus('saved');
      onIssued(issued.invitation.id, issued.link, issued.delivered ? null : (issued.notDeliveredReason ?? null));
      setRecipientName('');
      setEmail('');
      setPhone('');
    } catch (err: unknown) {
      setStatus('error');
      setError(errorMessage(err) ?? t('webAdmin.clientIntake.createError', { locale: LOCALE }));
    }
  }

  return (
    <div data-testid="client-intake-new">
      <label className="block mb-2">
        {t('webAdmin.clientIntake.form.recipientName', { locale: LOCALE })}
        <input
          className="rh-input w-full"
          value={recipientName}
          onChange={(event) => setRecipientName(event.target.value)}
          data-testid="client-intake-name"
        />
      </label>

      <label className="block mb-2">
        {t('webAdmin.clientIntake.form.role', { locale: LOCALE })}
        <select
          className="rh-input w-full"
          value={role}
          onChange={(event) => setRole(event.target.value as ClientRole)}
          data-testid="client-intake-role"
        >
          {ROLES.map((value) => (
            <option key={value} value={value}>
              {t(ROLE_KEY[value], { locale: LOCALE })}
            </option>
          ))}
        </select>
      </label>

      {/* Strana smlouvy: u zastupce firmy ji API vyzaduje, jinak se odvodi. */}
      <label className="block mb-2">
        {t('webAdmin.clientIntake.form.partyKey', { locale: LOCALE })}
        <select
          className="rh-input w-full"
          value={partyKey}
          onChange={(event) => setPartyKey(event.target.value as ClientPartyKey | '')}
          data-testid="client-intake-party-key"
        >
          <option value="">
            {t('webAdmin.clientIntake.form.partyKeyAuto', { locale: LOCALE })}
          </option>
          {PARTY_KEYS.map((value) => (
            <option key={value} value={value}>
              {t(PARTY_KEY_LABEL[value], { locale: LOCALE })}
            </option>
          ))}
        </select>
      </label>

      <label className="block mb-2">
        {t('webAdmin.clientIntake.form.partyIndex', { locale: LOCALE })}
        <input
          type="number"
          min={0}
          max={20}
          className="rh-input w-full"
          value={partyIndex}
          onChange={(event) => setPartyIndex(Number(event.target.value))}
        />
      </label>

      <label className="block mb-2">
        {t('webAdmin.clientIntake.form.email', { locale: LOCALE })}
        <input
          type="email"
          className="rh-input w-full"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          data-testid="client-intake-email"
        />
      </label>

      <label className="block mb-2">
        {t('webAdmin.clientIntake.form.phone', { locale: LOCALE })}
        <input
          className="rh-input w-full"
          value={phone}
          onChange={(event) => setPhone(event.target.value)}
          data-testid="client-intake-phone"
        />
      </label>

      <label className="block mb-2">
        {t('webAdmin.clientIntake.form.ttlDays', { locale: LOCALE })}
        <input
          type="number"
          min={1}
          max={30}
          className="rh-input w-full"
          value={ttlDays}
          onChange={(event) => setTtlDays(Number(event.target.value))}
          data-testid="client-intake-ttl"
        />
      </label>

      <label className="flex items-center gap-2 mb-3">
        <input
          type="checkbox"
          checked={requireSms}
          onChange={(event) => setRequireSms(event.target.checked)}
          data-testid="client-intake-require-sms"
        />
        {t('webAdmin.clientIntake.form.requireSms', { locale: LOCALE })}
      </label>

      <div className="flex gap-2 flex-wrap">
        {(['email', 'sms', 'odkaz'] as const).map((channel) => (
          <button
            key={channel}
            type="button"
            className="rh-btn rh-btn--secondary"
            disabled={recipientName.trim() === ''}
            onClick={() => void submit(channel)}
            data-testid={`client-intake-send-${channel}`}
          >
            {t(CHANNEL_KEY[channel], { locale: LOCALE })}
          </button>
        ))}
        <SaveStatus
          status={status}
          texts={{
            saving: t('webAdmin.clientIntake.saving', { locale: LOCALE }),
            saved: t('webAdmin.clientIntake.saved', { locale: LOCALE }),
            error: t('webAdmin.clientIntake.actionError', { locale: LOCALE }),
          }}
          {...(error !== undefined ? { errorDetail: error } : {})}
        />
      </div>
    </div>
  );
}

function InvitationDetail({
  invitationId,
  contractId,
  onChanged,
  onIssued,
}: {
  readonly invitationId: string;
  readonly contractId: string | undefined;
  readonly onChanged: () => void;
  readonly onIssued: (id: string, link: string, notDeliveredReason?: string | null) => void;
}): React.JSX.Element {
  const [detail, setDetail] = React.useState<ClientInvitationDetailDto | null>(null);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [status, setStatus] = React.useState<SaveStatusKind>('idle');
  const [overwrite, setOverwrite] = React.useState(false);
  const [lvFindings, setLvFindings] = React.useState<readonly LvFindingDto[] | null>(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  /** Krok KP2: vysledek posledniho vytezeni (pocty, ne hodnoty). */
  const [extractNote, setExtractNote] = React.useState<string | null>(null);
  /** Krok PROP2 bod 2: co se stalo v adresari po prevzeti podani. */
  const [crmNote, setCrmNote] = React.useState<readonly string[] | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setDetail(null);
    getClientInvitation(invitationId)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(errorMessage(err) ?? t('webAdmin.clientIntake.loadError', { locale: LOCALE }));
      });
    return () => {
      cancelled = true;
    };
  }, [invitationId, reloadKey]);

  async function act(work: () => Promise<void>): Promise<void> {
    setStatus('saving');
    setError(undefined);
    try {
      await work();
      setStatus('saved');
      setReloadKey((key) => key + 1);
      onChanged();
    } catch (err: unknown) {
      setStatus('error');
      setError(errorMessage(err) ?? t('webAdmin.clientIntake.actionError', { locale: LOCALE }));
    }
  }

  /**
   * Vlastnici z LV: berou se z hodnot smlouvy, ktere prisly z DAT REOS
   * (katastr), ne od klienta - jinak by se klient porovnaval sam se sebou.
   */
  async function compareWithLv(): Promise<void> {
    if (detail === null) return;
    let owners: { name: string; share: string | null }[] = [];
    if (contractId !== undefined) {
      const contract: ContractDetailDto = await getContract(contractId);
      owners = ownersFromContract(contract, detail.partyKey);
    }
    const findings = await compareInvitationWithLv(detail.id, owners);
    setLvFindings(findings);
  }

  if (error !== undefined && detail === null) {
    return (
      <p className="rh-text-danger" role="alert">
        {error}
      </p>
    );
  }
  if (detail === null) return <p>{t('common.loading', { locale: LOCALE })}</p>;

  const submission = detail.submission;
  return (
    <FormSection
      title={t('webAdmin.clientIntake.detailHeading', { locale: LOCALE })}
      description={`${detail.recipientName} · ${t(ROLE_KEY[detail.role], { locale: LOCALE })}`}
    >
      <dl className="grid grid-cols-2 gap-1 mb-3" data-testid="client-intake-detail">
        <dt>{t('webAdmin.clientIntake.table.status', { locale: LOCALE })}</dt>
        <dd>{t(STATUS_KEY[detail.status], { locale: LOCALE })}</dd>
        <dt>{t('webAdmin.clientIntake.table.channel', { locale: LOCALE })}</dt>
        <dd>{t(CHANNEL_KEY[detail.channel], { locale: LOCALE })}</dd>
        <dt>{t('webAdmin.clientIntake.table.expires', { locale: LOCALE })}</dt>
        <dd>{formatDate(detail.expiresAt)}</dd>
        <dt>{t('webAdmin.clientIntake.detail.smsVerified', { locale: LOCALE })}</dt>
        <dd>
          {detail.requireSms
            ? detail.smsVerified
              ? t('common.yes', { locale: LOCALE })
              : t('common.no', { locale: LOCALE })
            : '—'}
        </dd>
      </dl>

      <h4 className="font-medium mt-3">
        {t('webAdmin.clientIntake.timelineHeading', { locale: LOCALE })}
      </h4>
      <ol className="mb-3" data-testid="client-intake-timeline">
        {detail.timeline.map((entry, index) => {
          const key = TIMELINE_KEY[entry.kind];
          return (
            <li key={`${entry.kind}-${index}`}>
              {formatDate(entry.at)} — {key === undefined ? entry.kind : t(key, { locale: LOCALE })}
            </li>
          );
        })}
      </ol>

      {submission !== null && (
        <div data-testid="client-intake-submission">
          <h4 className="font-medium">
            {t('webAdmin.clientIntake.submissionHeading', { locale: LOCALE })}
          </h4>
          <dl className="grid grid-cols-2 gap-1">
            <dt>{t('webAdmin.clientIntake.submission.name', { locale: LOCALE })}</dt>
            <dd>{submission.displayName}</dd>
            <dt>{t('webAdmin.clientIntake.submission.email', { locale: LOCALE })}</dt>
            <dd>{submission.email}</dd>
            <dt>{t('webAdmin.clientIntake.submission.phone', { locale: LOCALE })}</dt>
            <dd>{submission.phone}</dd>
            <dt>{t('webAdmin.clientIntake.submission.address', { locale: LOCALE })}</dt>
            <dd>{submission.address}</dd>
            <dt>{t('webAdmin.clientIntake.submission.share', { locale: LOCALE })}</dt>
            <dd>{submission.share ?? '—'}</dd>
            {/* Citlive hodnoty chodi z API VZDY maskovane (bod 6). */}
            <dt>{t('webAdmin.clientIntake.submission.birthNumber', { locale: LOCALE })}</dt>
            <dd>{submission.birthNumberMasked ?? '—'}</dd>
            <dt>{t('webAdmin.clientIntake.submission.documentNumber', { locale: LOCALE })}</dt>
            <dd>{submission.documentNumberMasked ?? '—'}</dd>
            <dt>{t('webAdmin.clientIntake.submission.submittedAt', { locale: LOCALE })}</dt>
            <dd>{formatDate(submission.submittedAt)}</dd>
          </dl>

          {/*
            Krok NAP8 A18: karta „Ověřit doklad" stoji TADY, u cisla dokladu
            klienta — API vraci jen maskovanou podobu, takze cele cislo musi
            opsat clovek z dokladu a nikam se neuklada.
          */}
          <DocumentCheckCard />

          {/* Propadly doklad neni chyba formulare - je to prekazka u notare. */}
          {submission.documentExpired && (
            <p className="rh-text-warning" data-testid="client-intake-document-expired">
              {t('webAdmin.clientIntake.documentExpired', { locale: LOCALE })}
            </p>
          )}

          {/*
            NALEZ KROKU PIL2 (neopraveno zamerne — mimo zadani kroku).
            Tenhle seznam tiskne STROJOVE retezce („bankAccount —
            kontrolni-cislice"), kdezto klientsky formular je od kroku PIL2
            preklada (`klient.tsx`: `fieldLabel` + `codeLabel` nad klici
            `webPublic.clientIntake.*`). Je to tyz koncept na dvou mistech a
            tataz trida vady jako GEN1/O2 (strojovy klic na kokpitu). Sjednotit
            nejde pouhym importem: admin a verejny web jsou dva oddelene bundly
            (CLAUDE.md pravidlo 7) a mapa popisku poli dnes zije v komponente
            formulare, ne v katalogu. Oprava je samostatny krok: presunout mapu
            `pole -> popisek` do i18n a cist ji z obou aplikaci.
          */}
          {submission.issues.length > 0 && (
            <ul className="rh-text-danger" data-testid="client-intake-issues">
              {submission.issues.map((issue) => (
                <li key={issue.field}>{`${issue.field} — ${issue.code}`}</li>
              ))}
            </ul>
          )}

          {(lvFindings ?? submission.lvFindings).length > 0 && (
            <ul className="rh-text-warning" data-testid="client-intake-lv-findings">
              {(lvFindings ?? submission.lvFindings).map((finding) => (
                <li key={finding.code}>
                  {t(LV_FINDING_KEY[finding.code], { locale: LOCALE })}
                  {finding.closestLvOwner !== null ? ` (${finding.closestLvOwner})` : ''}
                </li>
              ))}
            </ul>
          )}
          {lvFindings !== null && lvFindings.length === 0 && (
            <p className="rh-text-muted" data-testid="client-intake-lv-ok">
              {t('webAdmin.clientIntake.lv.ok', { locale: LOCALE })}
            </p>
          )}

          {/* Krok KP2: dotaznik k nemovitosti a rozpory s LV / SVJ / zakazkou. */}
          <PropertySummary property={submission.property} sections={detail.propertySections} />
          <PropertyFindings findings={submission.propertyFindings} />
        </div>
      )}

      {detail.propertySections.includes('dokumenty') && (
        <IntakeDocuments
          documents={detail.documents}
          missing={detail.missingDocuments}
          extractNote={extractNote}
          onExtract={() =>
            void act(async () => {
              const result = await processInvitationDocuments(detail.id);
              setExtractNote(
                t('webAdmin.clientIntake.extractResult', {
                  locale: LOCALE,
                  values: {
                    attached: String(result.attached),
                    extracted: String(result.extracted),
                    notExtracted: String(result.notExtracted),
                  },
                }),
              );
            })
          }
        />
      )}

      <div className="flex gap-2 flex-wrap mt-3">
        <button
          type="button"
          className="rh-btn rh-btn--secondary"
          onClick={() =>
            void act(async () => {
              const issued = await remindClientInvitation(detail.id, {});
              onIssued(detail.id, issued.link, issued.delivered ? null : (issued.notDeliveredReason ?? null));
            })
          }
          data-testid="client-intake-remind"
        >
          {t('webAdmin.clientIntake.remindButton', { locale: LOCALE })}
        </button>
        <button
          type="button"
          className="rh-btn rh-btn--ghost"
          onClick={() =>
            void act(async () => {
              await revokeClientInvitation(
                detail.id,
                t('webAdmin.clientIntake.revokeReason', { locale: LOCALE }),
              );
            })
          }
          data-testid="client-intake-revoke"
        >
          {t('webAdmin.clientIntake.revokeButton', { locale: LOCALE })}
        </button>
        <button
          type="button"
          className="rh-btn rh-btn--ghost"
          onClick={() => void act(compareWithLv)}
          data-testid="client-intake-compare-lv"
        >
          {t('webAdmin.clientIntake.compareLvButton', { locale: LOCALE })}
        </button>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(event) => setOverwrite(event.target.checked)}
            data-testid="client-intake-overwrite"
          />
          {t('webAdmin.clientIntake.overwriteBrokerValues', { locale: LOCALE })}
        </label>
        <button
          type="button"
          className="rh-btn rh-btn--primary"
          disabled={submission === null || submission.submittedAt === null}
          onClick={() =>
            void act(async () => {
              const result = await confirmClientReceipt(detail.id, overwrite);
              setCrmNote(crmWriteNotes(result.crmWrite));
            })
          }
          data-testid="client-intake-confirm"
        >
          {t('webAdmin.clientIntake.confirmButton', { locale: LOCALE })}
        </button>
        <SaveStatus
          status={status}
          texts={{
            saving: t('webAdmin.clientIntake.saving', { locale: LOCALE }),
            saved: t('webAdmin.clientIntake.saved', { locale: LOCALE }),
            error: t('webAdmin.clientIntake.actionError', { locale: LOCALE }),
          }}
          {...(error !== undefined ? { errorDetail: error } : {})}
        />
      </div>
      {crmNote !== null && (
        <div data-testid="client-intake-crm-note">
          {crmNote.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      )}
    </FormSection>
  );
}

/**
 * Krok PROP2 bod 2 — CO SE STALO V ADRESARI, vetami pro maklere.
 *
 * Rozdil proti adresari se jen UKAZE; prepsat ho muze clovek v adresari,
 * protoze o tom, ktera hodnota je ta spravna, formular nerozhoduje.
 */
function crmWriteNotes(
  write: ConfirmReceiptResultDto['crmWrite'] | undefined,
): readonly string[] {
  // `undefined` = odpoved bez pole (starsi server nebo atrapa v testu); tvari
  // se stejne jako „nezapsalo se", ne jako vada obrazovky.
  if (write === null || write === undefined) return [];
  const lines: string[] = [];
  const added = String(write.addedContacts.length);
  lines.push(
    write.created
      ? t('webAdmin.clientIntake.crmWriteCreated', { locale: LOCALE, values: { added } })
      : t('webAdmin.clientIntake.crmWriteMatched', { locale: LOCALE, values: { added } }),
  );
  if (write.linkedToCase) {
    lines.push(t('webAdmin.clientIntake.crmWriteLinkedToCase', { locale: LOCALE }));
  }
  if (write.conflicts.length > 0) {
    const labels: Readonly<Record<'name' | 'email' | 'phone' | 'address', string>> = {
      name: t('webAdmin.clientIntake.crmWriteConflictName', { locale: LOCALE }),
      email: t('webAdmin.clientIntake.crmWriteConflictEmail', { locale: LOCALE }),
      phone: t('webAdmin.clientIntake.crmWriteConflictPhone', { locale: LOCALE }),
      address: t('webAdmin.clientIntake.crmWriteConflictAddress', { locale: LOCALE }),
    };
    const list = write.conflicts
      .map((conflict) =>
        t('webAdmin.clientIntake.crmWriteConflictItem', {
          locale: LOCALE,
          values: {
            field: labels[conflict.kind],
            existing: conflict.existing,
            submitted: conflict.submitted,
          },
        }),
      )
      .join('; ');
    lines.push(
      t('webAdmin.clientIntake.crmWriteConflicts', { locale: LOCALE, values: { list } }),
    );
  }
  return lines;
}

/**
 * Vlastnici z LV tak, jak je nesou hodnoty smlouvy z DAT REOS (zdroj
 * `reos_data` = katastr/RUIAN). Hodnoty od klienta se zamerne ignoruji.
 */
export function ownersFromContract(
  contract: ContractDetailDto,
  partyKey: ClientPartyKey,
): { name: string; share: string | null }[] {
  const values = contract.fieldValues;
  const listEntry = values[partyKey];
  if (
    listEntry !== undefined &&
    listEntry.source === 'reos_data' &&
    Array.isArray(listEntry.value)
  ) {
    return (listEntry.value as { jmeno?: unknown; podil?: unknown }[])
      .filter((item) => typeof item.jmeno === 'string' && item.jmeno !== '')
      .map((item) => ({
        name: String(item.jmeno),
        share: typeof item.podil === 'string' && item.podil !== '' ? item.podil : null,
      }));
  }
  const name = values[`${partyKey}[].jmeno`];
  const share = values[`${partyKey}[].podil`];
  if (name === undefined || name.source !== 'reos_data' || typeof name.value !== 'string')
    return [];
  return [
    {
      name: name.value,
      share:
        share !== undefined && typeof share.value === 'string' && share.value !== ''
          ? share.value
          : null,
    },
  ];
}
